import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, CheckCircle, Shield, Users, Trophy, BookOpen } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const EmailLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(errorMessage);
      
      // Handle device mismatch
      if (errorMessage.includes('Device mismatch')) {
        toast.error('You have been logged out from this device. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-700 p-3 sm:p-4">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        
        {/* Left Side - Welcome Content */}
        <div className="text-center lg:text-left text-white space-y-6 px-4 hidden lg:block">
          <div className="space-y-4">
            <div className="flex items-center gap-3 justify-center lg:justify-start">
              <img 
                src="https://customer-assets.emergentagent.com/job_cgda-exam-prep/artifacts/bek6ttpm_logo.png" 
                alt="Departmental Exam"
                className="h-16 sm:h-20 md:h-24 object-contain"
              />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
              India's First Mock Test Platform for Government Employees
            </h2>
            <p className="text-lg text-white/90">
              Join thousands of successful candidates who achieved their dreams with our comprehensive test preparation platform.
            </p>
          </div>

          {/* Security Badge */}
          <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4 backdrop-blur-sm">
            <Shield className="w-10 h-10 text-green-400" />
            <div>
              <p className="font-semibold">Secure Device-Bound Sessions</p>
              <p className="text-sm text-white/80">Your account is protected with single-device authentication</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 pt-4">
            <div className="text-center p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
              <Users className="w-8 h-8 mx-auto mb-2" />
              <div className="text-2xl font-bold">1K+</div>
              <div className="text-sm">Students</div>
            </div>
            <div className="text-center p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
              <BookOpen className="w-8 h-8 mx-auto mb-2" />
              <div className="text-2xl font-bold">500+</div>
              <div className="text-sm">Mock Tests</div>
            </div>
            <div className="text-center p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
              <Trophy className="w-8 h-8 mx-auto mb-2" />
              <div className="text-2xl font-bold">95%</div>
              <div className="text-sm">Success Rate</div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex justify-center">
          <Card className="w-full max-w-md shadow-2xl bg-white/95 backdrop-blur-sm border-0">
            <CardHeader className="space-y-2 sm:space-y-4 pb-3 sm:pb-6 text-center px-4 sm:px-6 pt-4 sm:pt-6">
              <button 
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 hover:text-gray-900 self-start"
                data-testid="back-to-home-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </button>
              
              {/* Mobile Logo */}
              <div className="flex justify-center lg:hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cgda-exam-prep/artifacts/bek6ttpm_logo.png" 
                  alt="Departmental Exam"
                  className="h-12 sm:h-16 object-contain rounded-lg"
                />
              </div>
              
              <div className="space-y-1">
                <CardTitle className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                  Welcome Back
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm text-gray-600">
                  Sign in with your email and password
                </CardDescription>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="w-3 h-3" />
                  <span>Encrypted</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-blue-600">
                  <Shield className="w-3 h-3" />
                  <span>Device Protected</span>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4 sm:space-y-5 px-4 sm:px-6 pb-4 sm:pb-6">
              <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
                <div className="space-y-1.5 sm:space-y-2">
                  <label htmlFor="email" className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-10 sm:h-12 text-sm sm:text-base pl-4 pr-4 border-2 border-gray-200 focus:border-blue-500 rounded-xl"
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-1.5 sm:space-y-2">
                  <label htmlFor="password" className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-10 sm:h-12 text-sm sm:text-base pl-4 pr-12 border-2 border-gray-200 focus:border-blue-500 rounded-xl"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-10 sm:h-12 text-sm sm:text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-xl shadow-lg"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Signing in...
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <div className="text-center space-y-3 sm:space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-6 sm:px-10 text-gray-500">New to CGDA SAS?</span>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate('/register')}
                  variant="outline"
                  className="w-full h-10 sm:h-12 text-sm sm:text-base font-semibold border-2 border-blue-200 text-blue-600 hover:bg-blue-50 rounded-xl"
                  data-testid="create-account-btn"
                >
                  Create New Account
                </Button>
              </div>

              {/* Security Notice - More compact on mobile */}
              <div className="p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 text-center leading-tight">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Single device login enforced. Logging in here will sign you out from other devices.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmailLoginPage;
